import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  FolderTask,
  FolderTaskDataType,
  FolderTaskStatus,
} from '../upload-mission/upload-mission.component';
import { FileSizePipe } from 'src/app/pipes/file-size.pipe';
import { TbsIconModule } from '@ngx-terabase/ui/icon';
import { TbsSelectModule } from '@ngx-terabase/ui/select';
import { TbsFormModule } from '@ngx-terabase/ui/form';
import { TbsProgressBarModule } from '@ngx-terabase/ui/progress-bar';
import { FormsModule } from '@angular/forms';
import { from, timer, retry, tap, defer, map, mergeMap } from 'rxjs';
import { UploadService } from 'src/app/services/upload.service';
import { TimeDurationPipe } from 'src/app/pipes/time-duration.pipe';
import { MissionCreationResult } from '../new-mission/new-mission.component';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { UploadStructureDialogComponent } from 'src/app/dialogs/upload-structure-dialog/upload-structure-dialog.component';

export type FolderItemEvent = 'success' | 'error' | 'recovered';

export type FolderItemUploadMetrics = {
  timeSpentUploading: number;
  uploadedSize: number;
  totalSize: number;
  uploadedNumberOfFiles: number;
  totalNumberOfFiles: number;
};

@Component({
  selector: 'app-folder-task-item',
  imports: [
    CommonModule,
    FileSizePipe,
    TbsIconModule,
    TbsSelectModule,
    TbsFormModule,
    FormsModule,
    TimeDurationPipe,
    TbsProgressBarModule,
  ],
  templateUrl: './folder-task-item.component.html',
  styleUrl: './folder-task-item.component.scss',
  standalone: true,
})
export class FolderTaskItemComponent implements OnInit {
  @Input() task!: FolderTask;
  @Input() public missionCreationResult!: MissionCreationResult;
  @Output() remove = new EventEmitter();
  @Output() uploadEvent = new EventEmitter<{ event: FolderItemEvent }>();
  @Output() dataTypeChange = new EventEmitter<FolderTaskDataType>();

  public metrics!: FolderItemUploadMetrics;
  public timeRemainingMs: number = 0;

  private uploadService = inject(UploadService);
  private dialog = inject(MatDialog);

  private uploadStartTime!: number;
  private smoothedSpeedBytesPerMs: number | null = null;
  private smoothingFactor = 0.1;

  ngOnInit(): void {
    this.metrics = this.initMetrics();
  }

  startUploading() {
    this.setStatus('uploading');
    this.uploadStartTime = Date.now();
    from(this.task.files)
      .pipe(
        mergeMap(
          (file) =>
            this.uploadFile(file).pipe(
              retry({
                delay: () => {
                  this.uploadEvent.emit({ event: 'error' });
                  return timer(10 * 1000).pipe(
                    tap(() => this.uploadEvent.emit({ event: 'recovered' }))
                  );
                },
                resetOnSuccess: true,
              })
            ),
          4
        )
      )
      .subscribe({
        next: (res) => this.updateMetrics(res),
        complete: () => {
          this.setStatus('uploaded');
          this.uploadEvent.emit({ event: 'success' });
        },
      });
  }

  onInfoIconClick() {
    this.dialog.open(UploadStructureDialogComponent, {
      width: '600px',
      data: { files: this.task.files },
    });
  }

  onDeleteIconClick(): void {
    this.remove.emit();
  }

  onDataTypeChange(event: FolderTaskDataType) {
    this.dataTypeChange.emit(event);
  }

  setStatus(status: FolderTaskStatus) {
    this.task.status = status;
  }

  uploadFile({
    originalFile,
    filePath,
  }: {
    originalFile: File;
    filePath: string;
  }) {
    return defer(() => {
      const startTime = Date.now();

      const folderBasePath = `${this.missionCreationResult.missionPath}/`;

      const suffixMap: Record<string, string> = {
        map: 'photos/mapping/',
        pano: 'photos/panoramic/',
        other: 'other_data/',
      };

      const suffix = this.task.dataType ? suffixMap[this.task.dataType] : '';

      const uploadPath = `${folderBasePath}${suffix}${filePath}`;

      return from(this.uploadService.uploadFile(originalFile, uploadPath)).pipe(
        map(() => {
          const durationMs = Date.now() - startTime;
          return { file: originalFile, durationMs };
        })
      );
    });
  }

  private initMetrics(): FolderItemUploadMetrics {
    return {
      timeSpentUploading: 0,
      uploadedNumberOfFiles: 0,
      totalNumberOfFiles: this.task.files.length,
      uploadedSize: 0,
      totalSize: this.task.files.reduce(
        (total, item) => total + item.originalFile.size,
        0
      ),
    };
  }

  private updateMetrics({
    file,
    durationMs,
  }: {
    file: File;
    durationMs: number;
  }) {
    this.metrics.timeSpentUploading += durationMs;
    this.metrics.uploadedNumberOfFiles++;
    this.metrics.uploadedSize += file.size;
    this.updateTimeRemaining();
  }

  private updateTimeRemaining() {
    const elapsedMs = Date.now() - this.uploadStartTime;

    const currentSpeed = this.metrics.uploadedSize / elapsedMs;

    if (this.smoothedSpeedBytesPerMs === null) {
      this.smoothedSpeedBytesPerMs = currentSpeed;
    } else {
      this.smoothedSpeedBytesPerMs =
        this.smoothingFactor * currentSpeed +
        (1 - this.smoothingFactor) * this.smoothedSpeedBytesPerMs;
    }

    const remainingBytesToUpload =
      this.metrics.totalSize - this.metrics.uploadedSize;

    const timeRemainingMs =
      remainingBytesToUpload / this.smoothedSpeedBytesPerMs;

    if (
      this.timeRemainingMs > 0 &&
      timeRemainingMs > this.timeRemainingMs * 2
    ) {
      this.timeRemainingMs = this.timeRemainingMs * 2;
    } else {
      this.timeRemainingMs = timeRemainingMs;
    }
  }
}
